import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FormOnSubmitType, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';

import {
  PossibleLogins,
  SelectedLogin,
} from '../PossibleLogins/PossibleLogins';
import { Logins } from '../../helpers/getPossibleLogins';
import { useGetLoginsForEmail } from '../../hooks/useGetLoginsForEmail';
import { getOrgIdForRedirect } from '../../helpers/getOrgIdForRedirect';
import { HandleIdpSsoLoginArgs } from '../UnifiedLogin/UnifiedLogin';

import {
  ForgotPasswordForm,
  ForgotPasswordInstance,
} from './ForgotPasswordForm';
import { ResetPasswordRequest } from './ResetPasswordRequest';

import './ForgotPasswordFlow.scss';

interface ForgotPasswordFlowProps {
  initialEmail: string | null;
  onBackToSignIn(email: string | null): void;
  handleIdpSsoLogin(args: HandleIdpSsoLoginArgs): void;
}

export const ForgotPasswordFlow = ({
  initialEmail,
  onBackToSignIn,
  handleIdpSsoLogin,
}: ForgotPasswordFlowProps) => {
  const { t } = useTranslation();
  const [emailForm] = OpForm.useForm<ForgotPasswordInstance>();

  const [email, setEmail] = React.useState(initialEmail);
  const [logins, setLogins] = React.useState<Logins[] | undefined>();
  const [selectedLogin, setSelectedLogin] = React.useState<
    SelectedLogin | undefined
  >();

  const { getLoginsForEmail, isSubmittingEmail } = useGetLoginsForEmail({
    emailForm,
    setLogins,
    setSelectedLogin,
    handleIdpSsoLogin,
  });

  const resetLogins = useCallback(() => {
    setLogins(undefined);
    setEmail(null);
  }, []);

  const onEmailSubmit: FormOnSubmitType = useCallback(
    async ({ values }) => {
      setEmail(values.email);
      getLoginsForEmail(values.email);
    },
    [setEmail, getLoginsForEmail],
  );

  // Should only show one
  const needsEmail = !logins && !selectedLogin;
  const needsNamespace = logins && email && !selectedLogin;
  const needsRequestPasswordChange = email && selectedLogin;

  return (
    <div className="forgot-password-page">
      {needsEmail && (
        <ForgotPasswordForm
          form={emailForm}
          isSubmitting={isSubmittingEmail}
          initialValues={{ email }}
          onSubmit={onEmailSubmit}
        />
      )}
      {needsNamespace && (
        <PossibleLogins
          email={email}
          logins={logins}
          isPasswordReset
          resetLogins={resetLogins}
          setSelectedLogin={setSelectedLogin}
          handleIdpSsoLogin={handleIdpSsoLogin}
        />
      )}
      {needsRequestPasswordChange && (
        <ResetPasswordRequest
          email={email}
          region={selectedLogin.regionEnv}
          orgId={getOrgIdForRedirect(null, selectedLogin.namespace)}
          namespaceNickname={selectedLogin.namespace!.nickname!}
          heliumEndpoint={selectedLogin.heliumEndpoint}
        />
      )}
      {needsRequestPasswordChange ? (
        <OpButton
          className="forgot-password-page__back-button-primary"
          type="primary"
          onClick={() => onBackToSignIn(email)}
        >
          {t('Back to sign in')}
        </OpButton>
      ) : (
        <OpTextButton
          className="forgot-password-page__back-button"
          onClick={() => onBackToSignIn(email)}
        >
          {t('Back to sign in')}
        </OpTextButton>
      )}
    </div>
  );
};
